import ApiRoundedIcon from '@mui/icons-material/ApiRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { getIntlLocale, useLocale } from '@/shared/i18n';
import { PageContainer } from '@/shared/ui';

type AuditEventKind = 'user' | 'system';
type AuditEventStatus = 'success' | 'warning' | 'error';

const AUDIT_EVENTS: Array<{
  id: string;
  at: string;
  kind: AuditEventKind;
  actor: string;
  source: string;
  action: string;
  status: AuditEventStatus;
}> = [
  {
    id: 'AUD-8451',
    at: '2026-08-07T14:42:11+03:00',
    kind: 'user',
    actor: 'Elena Morozova',
    source: 'merchant-portal',
    action: 'Updated price protection policy',
    status: 'success',
  },
  {
    id: 'AUD-8450',
    at: '2026-08-07T14:31:02+03:00',
    kind: 'system',
    actor: 'evotor-integration-service',
    source: 'Evotor cloud sync',
    action: 'Imported 128 receipts',
    status: 'success',
  },
  {
    id: 'AUD-8449',
    at: '2026-08-07T13:58:45+03:00',
    kind: 'user',
    actor: 'Ivan Petrov',
    source: 'merchant-portal',
    action: 'Changed stock for 7 SKUs',
    status: 'warning',
  },
  {
    id: 'AUD-8448',
    at: '2026-08-07T13:22:19+03:00',
    kind: 'system',
    actor: 'web-catalog-publication-service',
    source: 'Publication worker',
    action: 'Published TELEGRAM_MINI_APP_SHOP catalog',
    status: 'success',
  },
  {
    id: 'AUD-8447',
    at: '2026-08-07T12:47:05+03:00',
    kind: 'system',
    actor: 'web-price-engine-service',
    source: 'Pricing audit',
    action: 'Rejected suspicious price update',
    status: 'error',
  },
];

function dateTime(value: string): string {
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function auditStatusColor(status: AuditEventStatus): 'success' | 'warning' | 'error' {
  switch (status) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    default:
      return 'success';
  }
}

export function AuditPage() {
  const { t } = useTranslation('app');
  useLocale();
  const userEvents = AUDIT_EVENTS.filter((event) => event.kind === 'user').length;
  const systemEvents = AUDIT_EVENTS.length - userEvents;

  return (
    <PageContainer title={t('audit.title')} subtitle={t('audit.subtitle')} maxWidth={1280}>
      <Stack spacing={2.5}>
        <Alert severity="info" variant="outlined">
          {t('audit.demoNotice')}
        </Alert>

        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}
        >
          {[
            {
              key: 'events',
              label: t('audit.cards.events'),
              value: AUDIT_EVENTS.length,
              helper: t('audit.cards.eventsHelper'),
              icon: <HistoryRoundedIcon />,
              tone: 'primary' as const,
            },
            {
              key: 'users',
              label: t('audit.cards.users'),
              value: userEvents,
              helper: t('audit.cards.usersHelper'),
              icon: <PersonSearchRoundedIcon />,
              tone: 'info' as const,
            },
            {
              key: 'systems',
              label: t('audit.cards.systems'),
              value: systemEvents,
              helper: t('audit.cards.systemsHelper'),
              icon: <SyncAltRoundedIcon />,
              tone: 'secondary' as const,
            },
          ].map((card) => (
            <Card key={card.key}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h4">{card.value}</Typography>
                  </Box>
                  <Box
                    aria-hidden
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2.5,
                      display: 'grid',
                      placeItems: 'center',
                      color: `${card.tone}.main`,
                      bgcolor: (theme) => alpha(theme.palette[card.tone].main, 0.12),
                    }}
                  >
                    {card.icon}
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  {card.helper}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('audit.feedTitle')}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('audit.table.time')}</TableCell>
                  <TableCell>{t('audit.table.type')}</TableCell>
                  <TableCell>{t('audit.table.actor')}</TableCell>
                  <TableCell>{t('audit.table.source')}</TableCell>
                  <TableCell>{t('audit.table.action')}</TableCell>
                  <TableCell>{t('audit.table.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {AUDIT_EVENTS.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>{dateTime(event.at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={
                          event.kind === 'system' ? <ApiRoundedIcon /> : <PersonSearchRoundedIcon />
                        }
                        label={t(`audit.kind.${event.kind}`)}
                      />
                    </TableCell>
                    <TableCell>{event.actor}</TableCell>
                    <TableCell>{event.source}</TableCell>
                    <TableCell>{event.action}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={auditStatusColor(event.status)}
                        label={t(`audit.status.${event.status}`)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Stack>
    </PageContainer>
  );
}
